import React, { useEffect, useState } from 'react'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { t } from 'ttag'

import { ActivityDto } from '../../../../services/api.types'
import { darkBlue, accessibleGray, accessibleLighterGray } from '../../../../styles/material-ui-theme-v2'
import YumDrawer from '../../../../components/YumDrawer/YumDrawer'
import YumButtonGroup from '../../../../components/YumButtonGroup/YumButtonGroup'
import YumCancelButton from '../../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../../components/YumSubmitButton/YumSubmitButton'
import Loader from '../../../../components/Loader/Loader'
import ActivityItem from '../ActivityItem/ActivityItem'

interface ReviewDuplicatesModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Callback when the modal should be closed */
  onClose: () => void
  /** The full duplicate group (main + duplicates) */
  activities: ActivityDto[]
  /** Id of the member currently counted as the main */
  mainActivityId: string | null
  /** Whether the group is still loading */
  loading: boolean
  /** Whether a set-main request is in flight */
  settingMain: boolean
  /** Promote the given activity to the counted main */
  onConfirmMain: (activityId: string) => void
  /** Delete one of the duplicates */
  onDeleteActivity: (activityId: string) => void
}

const useStyles = makeStyles(() => ({
  content: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  helper: {
    fontSize: '14px',
    color: accessibleLighterGray,
    marginBottom: '16px',
  },
  card: {
    borderRadius: '12px',
    border: `2px solid ${accessibleGray}`,
    backgroundColor: 'white',
    padding: '4px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'border-color 150ms ease-in-out',
    '&.selected': {
      borderColor: darkBlue,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 8px 0',
  },
  activityPadding: {
    margin: '8px 8px -8px 8px',
  },
  selectHint: {
    fontSize: '12px',
    fontWeight: 600,
    color: accessibleLighterGray,
  },
  mainBadge: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'white',
    backgroundColor: darkBlue,
    borderRadius: '10px',
    padding: '2px 10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  loaderWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 0',
  },
}))

/**
 * Bottom-sheet modal for reviewing a group of duplicate activities. Lists the counted "main" plus
 * every activity de-duplicated against it, lets the user pick a different one as the main, and
 * delete unwanted duplicates. The current main can't be deleted here — to remove it, promote
 * another activity first.
 */
export const ReviewDuplicatesModal: React.FC<ReviewDuplicatesModalProps> = ({
  open,
  onClose,
  activities,
  mainActivityId,
  loading,
  settingMain,
  onConfirmMain,
  onDeleteActivity,
}) => {
  const classes = useStyles()
  const group = activities || []
  const [selectedId, setSelectedId] = useState<string | null>(mainActivityId)

  // Keep the selection in sync with the current main whenever the group (re)loads.
  useEffect(() => {
    setSelectedId(mainActivityId)
  }, [mainActivityId])

  const handleConfirm = () => {
    if (selectedId && selectedId !== mainActivityId) {
      onConfirmMain(selectedId)
    }
  }

  return (
    <YumDrawer
      disableSwipeToClose={true}
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        style: {
          background: 'white',
          borderTopLeftRadius: '30px',
          borderTopRightRadius: '30px',
          paddingTop: '20px',
          paddingRight: '20px',
          paddingLeft: '20px',
        },
      }}
    >
      <Typography variant="h6" style={{ marginBottom: '8px' }}>{t`Review duplicates`}</Typography>
      <Typography className={classes.helper}>
        {t`These activities overlap in time, so only one is counted. Pick the one to keep as the main — the others stay as hidden duplicates.`}
      </Typography>

      {loading ? (
        <div className={classes.loaderWrap}>
          <Loader top noPadding />
        </div>
      ) : (
        <div className={classes.content}>
          {group.map((activity) => {
            const isMain = activity.id === mainActivityId
            const isSelected = activity.id === selectedId
            return (
              <div
                key={activity.id}
                className={`${classes.card} ${isSelected ? 'selected' : ''}`}
                role="button"
                aria-pressed={isSelected}
                onClick={() => setSelectedId(activity.id)}
              >
                <div className={classes.cardHeader}>
                  <span className={classes.selectHint}>{isSelected ? t`Selected` : t`Tap to select`}</span>
                  {isMain && <span className={classes.mainBadge}>{t`Main`}</span>}
                </div>
                <div className={classes.activityPadding}>
                  <ActivityItem
                    activity={activity}
                    onDeleteActivity={isMain ? undefined : onDeleteActivity}
                    disableActions={isMain}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}

      <YumButtonGroup style={{ marginTop: '8px' }}>
        <YumCancelButton onClick={onClose} text={t`Cancel`} color="inherit" />
        <YumSubmitButton
          onClick={handleConfirm}
          text={t`Set as main`}
          submitInProgress={settingMain}
          disabled={loading || settingMain || !selectedId || selectedId === mainActivityId}
        />
      </YumButtonGroup>
    </YumDrawer>
  )
}

export default ReviewDuplicatesModal
